import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
  CubeIcon,
  ArrowPathIcon,
  SignalIcon,
  HomeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import { hasRole } from '../config/permissions';
import HomeDataDisplay from './HomeDataDisplay';
import SettingsMain from './Settings/SettingsMain';
import SupportDisplay from './SupportDisplay';

// Each card opens a live viewer. The viewers start their own data feed from
// the dryer when they open and stop it when they close — there is no manual
// subscribe step. 'custom' has no viewer component, so its card streams the
// raw feed inline while expanded.
const AVAILABLE_TOPICS = [
  {
    id: 'home',
    name: 'Home Dashboard',
    description: 'Live monitoring with charts and controls',
    icon: HomeIcon,
    color: 'blue'
  },
  {
    id: 'settings',
    name: 'Settings',
    description: 'Configure and manage device parameters',
    icon: Cog6ToothIcon,
    color: 'green'
  },
  {
    id: 'diagnostic',
    name: 'Support',
    description: 'System support, diagnostics and IO monitoring',
    icon: WrenchScrewdriverIcon,
    color: 'purple'
  },
  {
    id: 'custom',
    name: 'Custom Data',
    description: 'Custom device-specific data streams',
    icon: CubeIcon,
    color: 'orange'
  }
];

const colorClasses = {
  blue: { icon: 'text-blue-600', button: 'bg-blue-600 hover:bg-blue-700' },
  green: { icon: 'text-green-600', button: 'bg-green-600 hover:bg-green-700' },
  purple: { icon: 'text-purple-600', button: 'bg-purple-600 hover:bg-purple-700' },
  orange: { icon: 'text-orange-600', button: 'bg-orange-600 hover:bg-orange-700' }
};

const TopicCard = ({ topic, serial, isOnline, readOnly = false, model = '' }) => {
  const { subscribe, unsubscribe, getTopicData, connectionState } = useWebSocket();
  const [open, setOpen] = useState(false);

  const canOpen = isOnline && connectionState === 'connected';
  const colors = colorClasses[topic.color] || colorClasses.blue;
  const topicData = topic.id === 'custom' ? getTopicData(serial, 'custom') : null;

  // The custom feed has no viewer of its own, so the card starts and stops
  // the stream while its data panel is expanded.
  useEffect(() => {
    if (topic.id !== 'custom' || !open) return;
    subscribe(serial, 'custom');
    return () => unsubscribe(serial, 'custom');
  }, [topic.id, open, serial, subscribe, unsubscribe]);

  const handleClick = () => {
    if (!canOpen && !open) return;
    setOpen(topic.id === 'custom' ? !open : true);
  };

  const buttonLabel = topic.id === 'custom' && open ? 'Close' : 'Open';

  const renderCustomData = () => {
    if (!topicData || !topicData.data) {
      return (
        <div className="text-center py-8 text-gray-500">
          <ArrowPathIcon className="w-8 h-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">Waiting for data from the dryer...</p>
          <p className="text-xs text-gray-400 mt-2">
            If this stays empty while the dryer is online, the dryer is not
            sending this feed yet.
          </p>
        </div>
      );
    }

    const { data, timestamp } = topicData;

    return (
      <div className="space-y-3">
        <div className="flex justify-between items-center pb-2 border-b border-gray-100">
          <span className="text-xs text-gray-500">
            Last Updated: {format(new Date(timestamp), 'HH:mm:ss')}
          </span>
          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
            Live
          </span>
        </div>
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-green-400 text-xs font-mono">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-lg border border-gray-200 bg-white overflow-hidden"
    >
      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <topic.icon className={`w-6 h-6 ${colors.icon}`} />
          <div>
            <h4 className="font-semibold text-gray-900">{topic.name}</h4>
            <p className="text-sm text-gray-600 mt-1">{topic.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleClick}
            disabled={!canOpen && !open}
            className={`px-4 py-2 rounded-lg text-white text-sm font-medium
                       transition-all duration-200 ${colors.button}
                       ${!canOpen && !open ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {buttonLabel}
          </button>

          {!canOpen && (
            <span className="text-sm text-gray-500 ml-2">
              {!isOnline ? 'Dryer offline' : 'No live link to the cloud'}
            </span>
          )}
        </div>
      </div>

      {/* Inline data panel for the custom feed */}
      <AnimatePresence>
        {topic.id === 'custom' && open && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-gray-200"
          >
            <div className="p-4">{renderCustomData()}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full-screen modal for Home dashboard */}
      <AnimatePresence>
        {topic.id === 'home' && open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="fixed inset-4 md:inset-8 bg-white rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-center gap-3">
                    <HomeIcon className="w-6 h-6 text-blue-600" />
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Home Dashboard</h2>
                      <p className="text-sm text-gray-600">Device: {serial}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                  <HomeDataDisplay
                    deviceSerial={serial}
                    onClose={() => setOpen(false)}
                    readOnly={readOnly}
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence>
        {topic.id === 'settings' && open && (
          <SettingsMain
            deviceSerial={serial}
            onClose={() => setOpen(false)}
            readOnly={readOnly}
            model={model}
          />
        )}
      </AnimatePresence>

      {/* Support Modal */}
      <AnimatePresence>
        {topic.id === 'diagnostic' && open && (
          <SupportDisplay
            deviceSerial={serial}
            onClose={() => setOpen(false)}
            model={model}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const DeviceSubscriptions = ({ deviceSerial, isOnline, readOnly = false, model = '' }) => {
  const { connectionState, lastServerError } = useWebSocket();
  const { user } = useAuth();
  const [errorDismissedAt, setErrorDismissedAt] = useState(null);

  // The raw custom feed is a developer tool. A DM510's Support card opens a
  // single page read off its live feed (the panel's I/O is not on its Modbus map).
  const visibleTopics = AVAILABLE_TOPICS.filter(
    topic => topic.id !== 'custom' || hasRole(user?.role, 'developer')
  );

  const showError =
    lastServerError &&
    (!errorDismissedAt || lastServerError.timestamp > errorDismissedAt);

  return (
    <div className="space-y-4">
      {/* Live link status */}
      <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
        <SignalIcon className={`w-5 h-5 ${
          connectionState === 'connected' && isOnline ? 'text-green-600' : 'text-gray-400'
        }`} />
        <div>
          <p className="text-sm font-medium text-gray-900">Live link</p>
          <p className="text-xs text-gray-600">
            {connectionState === 'connected'
              ? isOnline
                ? 'Connected — live views available'
                : 'Connected to the cloud, but the dryer is offline'
              : 'Reconnecting to the cloud...'}
          </p>
        </div>
      </div>

      {/* Errors reported by the cloud (e.g. dryer not reachable) */}
      {showError && (
        <div className="flex items-start justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">
            {lastServerError.message}
            <span className="text-xs text-red-400 ml-2">
              {format(new Date(lastServerError.timestamp), 'HH:mm:ss')}
            </span>
          </p>
          <button
            onClick={() => setErrorDismissedAt(new Date().toISOString())}
            className="p-1 text-red-400 hover:text-red-600"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Viewer Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {visibleTopics.map(topic => (
          <TopicCard
            key={topic.id}
            topic={topic}
            serial={deviceSerial}
            isOnline={isOnline}
            readOnly={readOnly}
            model={model}
          />
        ))}
      </div>
    </div>
  );
};

export default DeviceSubscriptions;
